import * as React from "react";
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { AmountFormatCell } from "components/common/formatter";
import { NumericCell } from "../DibursementPayment/DisbursementInputField";
import { DateFormatCell } from "components/common/formatter";
import { process } from "@progress/kendo-data-query";
const initialDataState = {
  sort: [{ field: "date", dir: "asc" }],
  take: 50,
  skip: 0,
};

const PaySupplierGrid = React.memo((props) => {
  const [dataState, setDataState] = useState(initialDataState);
  const [data, setData] = useState([]);
  const prevAllocationsRef = useRef(null);
  const prevAmountRef = useRef(null);

  const dataStateChange = (e) => {
    if (
      e.dataState.sort !== undefined &&
      e.dataState.skip !== 0 &&
      dataState.sort !== e.dataState.sort
    ) {
      setDataState({ ...e.dataState, skip: 0 });
    } else if (e.dataState.sort[0] === undefined) {
      setDataState({
        ...e.dataState,
        sort: [
          {
            field: dataState.sort[0].field,
            dir: "asc",
          },
        ],
      });
    } else {
      setDataState(e.dataState);
    }
  };

  // Only update data when props.data reference actually changes
  useEffect(() => {
    if (props.data && props.data.length > 0) {
      setData(props.data);
    }
  }, [props.data]);

  // Memoize allocation calculation
  const allocations = useMemo(() => {
    return data
      .filter((item) => item.add === true)
      .map((item) => ({
        postingId: item.postingId,
        amount: item.amount,
        matterId: item.matterId,
      }));
  }, [data]);

  // Memoize total amount calculation
  const totalAmount = useMemo(() => {
    return data.reduce((total, item) => {
      if (item.add === true && item.amount !== null && item.amount !== undefined) {
        return total + parseFloat(item.amount);
      }
      return total;
    }, 0);
  }, [data]);

  // Only call parent callbacks when values actually change
  useEffect(() => {
    const allocationsString = JSON.stringify(allocations);
    if (prevAllocationsRef.current !== allocationsString) {
      prevAllocationsRef.current = allocationsString;
      props.updateAllocations(allocations);
    }
  }, [allocations]);

  useEffect(() => {
    if (prevAmountRef.current !== totalAmount) {
      prevAmountRef.current = totalAmount;
      props.setAmount(totalAmount);
    }
  }, [totalAmount]);

  const AmountNameHeader = useCallback((props) => {
    return (
      <div
        onClick={props.onClick}
        style={{ textAlign: "center", cursor: "pointer" }}>
        {props.title}
        {props.children}
      </div>
    );
  }, []);

  const itemChange = useCallback((e) => {
    setData((prevData) => {
      return prevData.map((item) => {
        if (item.transactionNo !== e.dataItem.transactionNo) {
          return item;
        }

        const updatedItem = { ...item };

        if (e.field === "amount") {
          const isValidPositive = e.value <= item.amountDue && item.amountDue >= 0;
          const isValidNegative =
            e.value < 0 && e.value > item.amountDue && item.amountDue < 0;

          if (isValidPositive || isValidNegative) {
            updatedItem.amount = e.value;
            // Auto-select if amount is entered
            if (e.value !== 0 && e.value !== null && !updatedItem.add) {
              updatedItem.add = true;
            }
            // Auto-deselect if amount is cleared
            if ((e.value === 0 || e.value === null) && updatedItem.add) {
              updatedItem.add = false;
            }
          }
        } else if (e.field === "add") {
          updatedItem.add = e.value;
          // Set amount to amountDue when selected, 0 when deselected
          updatedItem.amount = e.value ? item.amountDue : 0.0;
        }

        return updatedItem;
      });
    });
  }, []);

  return (
    <React.Fragment>
      <Grid
        data={process(data, dataState)}
        dataItemKey={"transactionNo"}
        onItemChange={itemChange}
        editField="inEdit"
        onDataStateChange={dataStateChange}
        pageable={true}
        {...dataState}
        sortable={true}
        className="flex-grow-1 overflow-auto grid-minwidth-col4-150">
        <Column
          field="date"
          title="Date"
          editable={false}
          width="100px"
          cells={{ data: DateFormatCell }}
          headerClassName="ps-2"
        />

        {props.type !== "invoices" && (
          <Column
            field="matterRef"
            title="Matter Ref"
            editable={false}
            width="100px"
            headerClassName="ps-2"
          />
        )}

        <Column
          field="reference"
          title="Ref"
          editable={false}
          width="110px"
          className="text-wrap fs-sm"
        />

        <Column
          field="details"
          title="Details"
          editable={false}
          className="text-wrap"
        />

        <Column
          field="vatAmount"
          title="VAT"
          editable={false}
          cells={{ data: AmountFormatCell }}
          headerCell={AmountNameHeader}
          width="100px"
        />

        <Column
          field="grossAmount"
          title="Gross"
          editable={false}
          cells={{ data: AmountFormatCell }}
          headerCell={AmountNameHeader}
          width="120px"
        />

        <Column
          field="amountPaid"
          title="Paid"
          editable={false}
          cells={{ data: AmountFormatCell }}
          headerCell={AmountNameHeader}
          width="120px"
        />

        <Column
          field="amountDue"
          title="Due"
          editable={false}
          cells={{ data: AmountFormatCell }}
          headerCell={AmountNameHeader}
          width="120px"
        />

        <Column
          field="add"
          title="Select"
          editor="boolean"
          width="80px"
          className="text-center"
        />

        <Column
          field="amount"
          title="Allocate"
          editor="numeric"
          width={"120px"}
          cells={{ data: NumericCell }}
          headerCell={AmountNameHeader}
          footerCell={() => (
            <td style={{ textAlign: "center", fontWeight: "bold" }}>
              Total: {totalAmount.toFixed(2)}
            </td>
          )}
        />
      </Grid>
    </React.Fragment>
  );
});

PaySupplierGrid.displayName = "PaySupplierGrid";

export default PaySupplierGrid;
